import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { ElectionSelector } from '@/shared/election/ElectionSelector'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  ChevronDown, ChevronRight, ChevronLeft, Search, Bell, Settings, HelpCircle,
  Menu, LogOut, Lock,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { config } from '@/config/runtime-config'
import { useSession, can } from '@/shared/auth/session'
import { pingApi } from '@/shared/api/client'
import { NAV_ITEMS, GROUPS, TAG_STYLES, isLive, type NavGroup, type NavItem } from './nav'
import { useIntegrationNavEntries } from '@/features/integrations/navEntries'
import { OnAirBar } from '@/features/coverage/OnAirBar'
import { ThemeToggle } from '@/shared/ui/ThemeToggle'

/**
 * PORTED from pollsphere-bundle/src/components/tessera/app-shell.tsx.
 *
 * Structure, class strings, spacing, wording and the h-12 / h-9 / h-7 band
 * heights are PollSphere's, kept as-is. Additions are limited to:
 *   · `axis-*` classes that add off-canvas behaviour under 1024px
 *   · routing (PollSphere switched modules with local state; we use real URLs)
 *   · permission filtering from the backend instead of a hardcoded matrix
 */
/**
 * A sidebar sub-item, whether it came from `nav.ts` or from the API.
 * `muted` marks something real but not yet configured — recessive, still
 * clickable. `phase` marks something that does not exist yet — dimmed and dead.
 * The two look similar and mean opposite things, so they are separate fields.
 */
interface RenderChild {
  id: string
  label: string
  to?: string
  badge?: string
  section?: string
  phase?: number
  muted: boolean
  health?: string
  status?: string
  /** A placeholder row: still loading, or the fetch failed. Never clickable. */
  notice?: 'loading' | 'error'
}

export function AppShell({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useState(false)
  const [navOpen, setNavOpen] = useState(false)
  const [collapsedGroups, setCollapsedGroups] = useState<Partial<Record<NavGroup, boolean>>>({})
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({})
  const [apiUp, setApiUp] = useState<boolean | null>(null)

  const user = useSession((s) => s.user)
  const authorities = useSession((s) => s.authorities)
  const clear = useSession((s) => s.clear)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => { document.documentElement.classList.toggle('nav-open', navOpen) }, [navOpen])
  useEffect(() => { setNavOpen(false) }, [location.pathname, location.search])
  useEffect(() => {
    let alive = true
    const check = () => { void pingApi().then((s) => alive && setApiUp(s === 'up')) }
    check()
    const t = setInterval(check, 60_000)
    return () => { alive = false; clearInterval(t) }
  }, [])

  const visible = (i: NavItem) => !i.permission || can(authorities, i.permission)

  const grouped = useMemo(
    () => GROUPS.map((g) => ({
      ...g,
      items: NAV_ITEMS.filter((i) => i.group === g.id && (isLive(i) ? visible(i) : true)),
    })).filter((g) => g.items.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authorities],
  )

  /*
   * Exact match first, then the LONGEST prefix — and never the bare '/'.
   *
   * The old fallback was `startsWith(i.to)` over the list in order, and the first item is
   * the Command Centre at '/'. Every path starts with '/', so every sub-page matched it:
   * the top bar said "Command Centre" on all of them and the sidebar highlighted the
   * dashboard while you stood in the war room. Longest-prefix also picks
   * '/station-mates/board' over '/station-mates' when both would match.
   */
  const here = location.pathname + location.search
  const activeItem = NAV_ITEMS.find((i) => i.to && here === i.to)
    ?? NAV_ITEMS
      .filter((i) => {
        const base = i.to?.split('?')[0]
        return base && base !== '/'
          && (location.pathname === base || location.pathname.startsWith(base + '/'))
      })
      .sort((a, b) => (b.to?.length ?? 0) - (a.to?.length ?? 0))[0]
    ?? NAV_ITEMS.find((i) => i.to === '/' && location.pathname === '/')

  // One fetch per session, gated on the authority the endpoints actually
  // require: without it both calls 403 and the group would render a row of
  // failures instead of simply not expanding.
  const integrationNav = useIntegrationNavEntries(can(authorities, 'integration:read'))

  /**
   * Static children from `nav.ts`, or fetched ones for an item marked
   * `dynamic`. Kept as one function so the rendering below stays unaware of
   * where a child came from.
   */
  const childrenOf = (item: NavItem): RenderChild[] => {
    if (item.dynamic !== 'integrations') {
      // A child may need a NARROWER authority than its parent — the programmes
      // group is `program:read` and its analytics screens are
      // `program:analytics`. Without this filter a reader holding only the
      // former was offered eleven links that all rendered a Forbidden page.
      return (item.children ?? [])
        .filter((c) => !c.permission || can(authorities, c.permission))
        .map((c) => ({ ...c, muted: false }))
    }
    // A placeholder while the catalogue is in flight, and a stated failure if it
    // never arrives. Returning an empty array instead would make the group look
    // childless: no chevron, nothing to expand, and a 500 indistinguishable
    // from "there are no integrations".
    if (integrationNav.entries.length === 0 && (integrationNav.loading || integrationNav.error)) {
      return [{
        id: `integrations-${integrationNav.error ? 'error' : 'loading'}`,
        label: integrationNav.error ? 'Could not load the catalogue' : 'Loading…',
        muted: true,
        notice: integrationNav.error ? 'error' : 'loading',
      }]
    }
    return integrationNav.entries.map((e) => ({
      id: e.key,
      label: e.label,
      to: e.to,
      badge: e.badge,
      muted: !e.registered,
      health: e.health,
      status: e.status,
    }))
  }

  /**
   * `current` is the value actually on screen, which is not always
   * `expandedItems[id]` — a group can be open because the route is inside it
   * while its entry is still undefined. Toggling `!prev[id]` in that state
   * writes `true` over an already-open group, so the first click does nothing
   * visible.
   */
  const toggleItem = (id: string, current: boolean) =>
    setExpandedItems((prev) => ({ ...prev, [id]: !current }))

  function select(item: NavItem) {
    if (!isLive(item) || !item.to) return
    navigate(item.to)
  }

  return (
    <div className="axis-shell flex h-screen w-full bg-background overflow-hidden text-foreground">
      <div className="axis-scrim" onClick={() => setNavOpen(false)} aria-hidden />

      {/* ============== Sidebar ============== */}
      <aside className={cn(
        'axis-sidebar flex flex-col bg-sidebar border-r border-sidebar-border shrink-0 transition-all duration-200',
        collapsed ? 'w-16' : 'w-64',
      )}>
        {/* Brand bar */}
        <div className="h-12 flex items-center gap-2.5 px-3 border-b border-sidebar-border bg-primary text-primary-foreground">
          <div className="h-7 w-7 rounded-md bg-primary-foreground/15 flex items-center justify-center font-mono text-erp-md font-bold shrink-0">
            {config.brandMark}
          </div>
          {!collapsed && (
            <div className="flex flex-col leading-none min-w-0">
              <span className="text-erp-md font-semibold tracking-tight truncate">{config.appName}</span>
              <span className="text-erp-xs uppercase tracking-wider text-primary-foreground/70 truncate mt-0.5">
                {config.appTagline}
              </span>
            </div>
          )}
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="axis-icon-btn ml-auto h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-primary-foreground/15 transition-colors"
            title={collapsed ? 'Expand' : 'Collapse'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>

        {/* Scope selector */}
        {!collapsed && (
          <div className="px-3 py-2.5 border-b border-sidebar-border">
            <button className="w-full flex items-center gap-2 px-2.5 py-1.5 text-erp-sm rounded-md border border-border bg-background hover:bg-accent/60 text-left transition-colors">
              <span className="erp-status-dot bg-emerald-500" />
              <span className="flex-1 truncate text-foreground font-medium">
                {!user?.scopeLevel || user.scopeLevel === 'NATIONAL'
                  ? 'National · All Counties'
                  : `${user.scopeLevel} scope`}
              </span>
              {user?.scopeLevel && user.scopeLevel !== 'NATIONAL' && <Lock className="h-3 w-3 text-amber-600" />}
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground transition-transform" />
            </button>
          </div>
        )}

        {/* Nav — collapsible groups with sub-items */}
        <nav className="flex-1 overflow-y-auto py-2">
          {grouped.map((g) => {
            const isGroupCollapsed = collapsedGroups[g.id]
            return (
              <div key={g.id} className="mb-1.5">
                {!collapsed && (
                  <button
                    onClick={() => setCollapsedGroups((prev) => ({ ...prev, [g.id]: !prev[g.id] }))}
                    className="w-full flex items-center gap-1.5 px-3 py-1.5 text-erp-xs font-semibold uppercase tracking-wider text-muted-foreground/80 hover:text-foreground transition-colors"
                  >
                    {isGroupCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    <span className="flex-1 text-left">{g.label}</span>
                  </button>
                )}
                {collapsed && (
                  <div className="px-3 py-1.5 text-erp-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
                    <g.icon className="h-3.5 w-3.5" />
                  </div>
                )}
                {(!isGroupCollapsed || collapsed) && g.items.map((item) => {
                  const Icon = item.icon
                  const live = isLive(item)
                  const isActive = activeItem?.id === item.id
                  const kids = childrenOf(item)
                  const hasChildren = kids.length > 0
                  const canExpand = hasChildren || item.dynamic === 'integrations'
                  // A dynamic group opens itself when you are already inside it,
                  // so arriving on an integration by URL does not leave its own
                  // name hidden behind a collapsed parent.
                  const insideDynamic =
                    item.dynamic === 'integrations' && location.pathname.startsWith('/integrations/')
                  const isExpanded = expandedItems[item.id] ?? insideDynamic
                  return (
                    <div key={item.id}>
                      <button
                        onClick={() => {
                          // Geography is the first item that is BOTH live and has
                          // children. Navigate when it has a route, and expand too —
                          // otherwise the parent becomes an accordion handle only
                          // and its own screen is unreachable from the sidebar.
                          if (live && item.to) select(item)
                          // `canExpand`, not `hasChildren`: for a dynamic group
                          // the children may still be loading, and swallowing
                          // the click means the user has to press it twice.
                          if (canExpand) toggleItem(item.id, isExpanded)
                        }}
                        disabled={!live && !canExpand}
                        title={collapsed ? item.label : !live ? `${item.label} — Phase ${item.phase}` : undefined}
                        className={cn(
                          'axis-nav-item w-full flex items-center gap-2.5 px-3 py-2 text-erp-sm border-l-[3px] transition-colors',
                          isActive
                            ? 'border-l-chocolate bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                            : 'border-l-transparent text-sidebar-foreground hover:bg-sidebar-accent/50',
                          !live && 'opacity-45 cursor-default hover:bg-transparent',
                        )}
                      >
                        <Icon className={cn('h-4 w-4 shrink-0', isActive ? 'text-chocolate' : 'text-muted-foreground')} />
                        {!collapsed && (
                          <>
                            <span className="flex-1 text-left truncate">{item.label}</span>
                            {item.tag && (
                              <span className={cn('text-erp-xs px-1.5 py-0.5 rounded-md border font-semibold', TAG_STYLES[item.tag])}>
                                {item.tag}
                              </span>
                            )}
                            {item.badge && (
                              <span className="text-erp-xs px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground font-mono">
                                {item.badge}
                              </span>
                            )}
                            {!live && !item.tag && !item.badge && (
                              <span className="text-erp-xs px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground font-mono">
                                P{item.phase}
                              </span>
                            )}
                            {canExpand && (
                              <span className="h-4 w-4 inline-flex items-center justify-center rounded text-muted-foreground">
                                {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                              </span>
                            )}
                          </>
                        )}
                      </button>

                      {/* Sub-items */}
                      {!collapsed && isExpanded && hasChildren && (
                        <div className="bg-sidebar-accent/20 border-l-[3px] border-l-transparent">
                          {kids.map((child) => {
                            if (child.section) {
                              return (
                                <div
                                  key={`sec-${child.section}-${child.id}`}
                                  className="px-9 pt-2.5 pb-0.5 text-erp-xs uppercase tracking-wider text-muted-foreground/70 font-semibold border-l-[3px] -ml-[3px] border-l-transparent"
                                >
                                  {child.section}
                                </div>
                              )
                            }
                            // A child with a route is live; the rest stay dimmed
                            // with their phase, exactly as before.
                            const childLive =
                              Boolean(child.to) && child.phase === undefined && !child.notice
                            // A child whose route carries no query matches on
                            // pathname alone. The detail page writes `?tab=…` on
                            // every tab click, so an exact match would unhighlight
                            // the row the moment you looked at its report — and on
                            // the registration path, the integration you just
                            // created is the one row not highlighted.
                            // Children that DO carry a query (Geography's five
                            // `?type=` links share one pathname) still match exactly.
                            const childActive = child.to
                              ? child.to.includes('?')
                                ? location.pathname + location.search === child.to
                                : location.pathname === child.to
                              : false
                            return (
                              <button
                                key={child.id}
                                disabled={!childLive}
                                onClick={childLive ? () => navigate(child.to!) : undefined}
                                // The dot's colour is never the only carrier of
                                // health — the word is in the tooltip here and
                                // spelled out on the integration's own page.
                                title={childTitle(child)}
                                className={cn(
                                  'w-full flex items-center gap-2 pl-9 pr-3 py-1.5 text-erp-sm border-l-[3px] -ml-[3px]',
                                  childActive
                                    ? 'border-l-chocolate bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                                    : 'border-l-transparent',
                                  !childLive && 'text-muted-foreground opacity-55 cursor-default',
                                  // Recessive, not dead: an unconfigured provider
                                  // is one click from working, so it keeps its
                                  // hover and its route.
                                  childLive && child.muted && !childActive
                                    && 'text-muted-foreground/75 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground',
                                  childLive && !child.muted && !childActive
                                    && 'text-sidebar-foreground hover:bg-sidebar-accent/50',
                                )}
                              >
                                <StatusDot
                                  muted={child.muted}
                                  health={child.health}
                                  status={child.status}
                                  notice={child.notice}
                                />
                                <span className="flex-1 text-left truncate">{child.label}</span>
                                {child.badge && (
                                  <span className="text-erp-xs px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground font-mono">
                                    {child.badge}
                                  </span>
                                )}
                              </button>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </nav>

        {/* Footer */}
        {!collapsed && (
          <div className="border-t border-sidebar-border px-3 py-2.5 text-erp-xs text-muted-foreground space-y-1">
            <div className="flex justify-between"><span>Build</span><span className="font-mono">{config.release}</span></div>
            <div className="flex justify-between items-center">
              <span>Axis API</span>
              <span className={cn('inline-flex items-center gap-1', apiUp ? 'text-emerald-600' : 'text-red-600')}>
                <span className={cn('erp-status-dot', apiUp ? 'bg-emerald-500' : 'bg-red-500')} />
                {apiUp === null ? 'Checking…' : apiUp ? 'Connected' : 'Unreachable'}
              </span>
            </div>
            <div className="flex justify-between items-center pt-1 border-t border-sidebar-border/60">
              <span>Clearance</span>
              <span className="text-amber-600">{user?.clearance ?? '—'}</span>
            </div>
          </div>
        )}
      </aside>

      {/* ============== Main column ============== */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="axis-topbar h-12 flex items-center gap-3 px-4 border-b border-border bg-card shrink-0">
          <button
            className="axis-menu-btn axis-icon-btn h-9 w-9 items-center justify-center rounded-md hover:bg-muted"
            onClick={() => setNavOpen(true)}
            aria-label="Open navigation"
          >
            <Menu className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-1.5 text-erp-sm text-muted-foreground min-w-0">
            <Menu className="h-4 w-4 axis-hide-sm" />
            <span className="axis-hide-sm">{config.appName}</span>
            <span className="text-border axis-hide-sm">/</span>
            <span className="text-foreground font-medium truncate">{activeItem?.label ?? 'Module'}</span>
          </div>

          {/* Search */}
          <div className="ml-4 flex items-center gap-2 px-3 h-9 w-80 border border-input rounded-md bg-background focus-within:ring-2 focus-within:ring-ring/30 focus-within:border-ring transition axis-hide-sm">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search users, roles, settings… (Ctrl+K)"
              className="flex-1 bg-transparent outline-none text-erp-sm placeholder:text-muted-foreground"
            />
          </div>

          <div className="ml-auto flex items-center gap-1.5">
            <button className="h-9 px-2.5 inline-flex items-center gap-1.5 text-erp-sm text-muted-foreground hover:bg-muted rounded-md transition-colors relative" aria-label="Notifications">
              <Bell className="h-4 w-4" />
            </button>
            {/* Theme is the one control here that is used often and is not reachable
                anywhere else, so it stays visible at every width — unlike Settings and
                Help, which carry `axis-hide-sm`. */}
            <ThemeToggle />
            <button className="h-9 w-9 inline-flex items-center justify-center text-muted-foreground hover:bg-muted rounded-md transition-colors axis-hide-sm" aria-label="Settings">
              <Settings className="h-4 w-4" />
            </button>
            <button className="h-9 w-9 inline-flex items-center justify-center text-muted-foreground hover:bg-muted rounded-md transition-colors axis-hide-sm" aria-label="Help">
              <HelpCircle className="h-4 w-4" />
            </button>
            <div className="h-6 w-px bg-border mx-1.5 axis-hide-sm" />
            <details className="relative">
              <summary className="h-9 px-2 inline-flex items-center gap-2 rounded-md hover:bg-muted cursor-pointer list-none">
                <span className="h-6 w-6 rounded-md bg-chocolate text-chocolate-foreground inline-flex items-center justify-center text-erp-xs font-bold">
                  {(user?.name ?? user?.email ?? '?').slice(0, 1).toUpperCase()}
                </span>
                <span className="text-erp-sm font-medium max-w-[140px] truncate axis-hide-sm">{user?.name ?? '—'}</span>
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground axis-hide-sm" />
              </summary>
              <div className="absolute right-0 mt-1 w-56 erp-card shadow-lg z-50 p-1">
                <div className="px-3 py-2 border-b border-border">
                  <div className="text-erp-sm font-semibold truncate">{user?.name}</div>
                  <div className="text-erp-xs text-muted-foreground truncate">{user?.email}</div>
                </div>
                <button
                  className="w-full text-left px-3 py-2 text-erp-sm hover:bg-muted rounded-md inline-flex items-center gap-2"
                  onClick={() => { clear(); navigate('/login', { replace: true }) }}
                >
                  <LogOut className="h-3.5 w-3.5" /> Sign out
                </button>
              </div>
            </details>
          </div>
        </header>

        {/* Module sub-bar */}
        <div className="axis-subbar h-9 flex items-center gap-3 px-4 border-b border-border bg-muted/40 text-erp-sm shrink-0 overflow-x-auto">
          {/* Was hardcoded "2027 General Election". The slot was right; the content said
              the same thing whatever the platform held or the user was looking at. */}
          <span className="text-muted-foreground whitespace-nowrap">Election Cycle:</span>
          <ElectionSelector />
          <span className="text-border axis-subbar-extra">·</span>
          <span className="text-muted-foreground axis-subbar-extra whitespace-nowrap">Viewing:</span>
          <span className="font-medium text-chocolate axis-subbar-extra whitespace-nowrap">
            {!user?.scopeLevel || user.scopeLevel === 'NATIONAL' ? 'National · All Counties' : user.scopeLevel}
          </span>
          <span className="ml-auto text-muted-foreground inline-flex items-center gap-1.5 axis-subbar-extra whitespace-nowrap">
            <span className={cn('erp-status-dot', apiUp ? 'bg-emerald-500' : 'bg-red-500')} />
            {apiUp ? 'API connected' : 'API unreachable'}
          </span>
        </div>

        {/*
          On air, everywhere. The broadcast session outlives the coverage screens,
          so the one state that must never be forgotten follows the user out of
          them. Renders nothing unless this tab is publishing.
        */}
        <OnAirBar />

        {/* Content area */}
        <main className="flex-1 overflow-y-auto bg-background p-4">{children}</main>

        {/* Status bar */}
        <footer className="axis-statusbar h-7 flex items-center gap-3 px-4 border-t border-border bg-card text-erp-xs text-muted-foreground font-mono shrink-0 overflow-hidden">
          <span className="truncate">{config.appName} Enterprise · Elections Intelligence Suite</span>
          <span className="text-border axis-statusbar-extra">|</span>
          <span className="axis-statusbar-extra">User:</span>
          <span className="text-foreground font-semibold axis-statusbar-extra truncate">{user?.name ?? '—'}</span>
          <span className="text-border axis-statusbar-extra">|</span>
          <span className="axis-statusbar-extra">Unit:</span>
          <span className="text-foreground axis-statusbar-extra">
            {!user?.scopeLevel || user.scopeLevel === 'NATIONAL' ? 'National' : user.scopeLevel}
          </span>
          <span className="ml-auto axis-statusbar-extra">{config.environment.toUpperCase()}</span>
        </footer>
      </div>
    </div>
  )
}

/**
 * The bullet beside a sub-item.
 *
 * PollSphere's is a plain `h-1 w-1 rounded-full bg-current opacity-40`, and for
 * an ordinary healthy integration that is exactly what renders — the sidebar
 * should not turn into a status board. Two departures, both carrying meaning the
 * flat dot cannot:
 *
 *  - **Unconfigured** is a hollow ring. An empty outline reads as "a slot, not
 *    yet filled" without needing colour, which matters because this state is the
 *    most common one on a fresh install.
 *  - **Degraded or down** tints the dot, and the word appears in the row's
 *    tooltip and on the integration's page. Colour alone is never the carrier;
 *    it is the thing that makes you look.
 */
/**
 * What a sidebar row says when you hover it.
 *
 * Every state that is not "a healthy, configured integration" has to be
 * legible without colour, because the dot beside the label is one and a half
 * pixels of it and colour is never the only carrier of meaning.
 */
function childTitle(child: RenderChild): string {
  if (child.notice === 'error') return 'The integration catalogue could not be loaded'
  if (child.notice === 'loading') return 'Loading the integration catalogue'
  if (!child.to) return `${child.label} — Phase ${child.phase}`
  if (child.muted) return `${child.label} — not configured yet`
  if (child.status === 'DRAFT') return `${child.label} — draft, no credentials stored yet`
  if (child.status === 'DISABLED') return `${child.label} — disabled`
  if (child.health && child.health !== 'HEALTHY') return `${child.label} — ${child.health.toLowerCase()}`
  return child.label
}

/**
 * The bullet beside a sub-item.
 *
 * PollSphere's is a plain `h-1 w-1 rounded-full bg-current opacity-40`, and for
 * an ordinary healthy integration that is exactly what renders — the sidebar
 * should not turn into a status board. The departures each carry meaning the
 * flat dot cannot:
 *
 *  - **Unconfigured** is a hollow ring. An empty outline reads as "a slot, not
 *    yet filled" without needing colour, which matters because this state is the
 *    most common one on a fresh install.
 *  - **Draft or disabled** is also hollow: registered, but not carrying traffic.
 *    A filled dot on a draft would make a integration with no credentials look
 *    exactly like a working one, and "why is nothing coming through" is an
 *    expensive question to have to ask.
 *  - **Degraded or down** tints the dot, and the word appears in the row's
 *    tooltip and on the integration's page. Colour alone is never the carrier;
 *    it is the thing that makes you look.
 */
function StatusDot({
  muted, health, status, notice,
}: {
  muted: boolean
  health?: string
  status?: string
  notice?: 'loading' | 'error'
}) {
  if (notice) {
    return (
      <span
        aria-hidden
        className={cn(
          'h-1.5 w-1.5 rounded-full shrink-0',
          notice === 'error' ? 'bg-red-600' : 'bg-current opacity-30 animate-pulse',
        )}
      />
    )
  }
  // Not live: nothing configured, or configured but deliberately not running.
  if (muted || status === 'DRAFT' || status === 'DISABLED') {
    return (
      <span
        aria-hidden
        className="h-1.5 w-1.5 rounded-full border border-current opacity-40 shrink-0"
      />
    )
  }
  return (
    <span
      aria-hidden
      className={cn(
        'h-1 w-1 rounded-full shrink-0',
        health === 'DOWN' ? 'bg-red-600 opacity-100'
          : health === 'DEGRADED' ? 'bg-amber-500 opacity-100'
            : 'bg-current opacity-40',
      )}
    />
  )
}
